export interface CreateContactRequestPayload {
  subject: string;
  description: string;
}

export interface CreateContactRequestResult {
  success: boolean;
  contactRequestId?: string;
  error?: string;
}

export type ContactRequestStatus =
  | 'pending'
  | 'in_review'
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

/** Statuses from which no further transitions are permitted. */
export const TERMINAL_STATUSES: ReadonlyArray<ContactRequestStatus> = [
  'completed',
  'cancelled',
];

/**
 * A fully-described media resource sent in the completion `response.attachments`
 * array (RR-1097). Shape matches the backend `MediaResource` contract: a file
 * already uploaded to S3 via the presigned-URL flow.
 */
export interface ContactRequestAttachmentMedia {
  isUploaded: true;
  /** S3 object key returned by the presigned-URL flow (no leading slash). */
  path: string;
  /** Media resource UUID (generated by the backend during presign). */
  resourceId: string;
  filename: string;
  fileType: string;
  fileSize: number;
}

/**
 * The client-facing "response" sent when an assigned admin completes a contact
 * request (RR-1097): an optional note plus up to 5 attachments. Only consumed by
 * the backend on the `completed` transition; ignored for any other status.
 */
export interface ContactRequestCompletionResponse {
  /** Trimmed client-facing note, or `null` when empty. */
  notes: string | null;
  attachments: ContactRequestAttachmentMedia[];
}

export interface UpdateContactRequestStatusPayload {
  new_status: ContactRequestStatus;
  note?: string;
  response?: ContactRequestCompletionResponse;
}

export interface ContactRequestListItem {
  id: string;
  contact_request_id: string;
  subject: string;
  status: ContactRequestStatus;
  assigned_to: string | null;
  /** Sequential id of the assigned admin, e.g. ADMIN-00001. */
  assigned_user_id?: string | null;
  assigned_user_name?: string | null;
  assigned_user_email?: string | null;
  created_at: string;
  client_name: string;
  client_user_id: string;
  client_email: string;
  client_phone: string;
  client_profile_url?: string | null;
}

export interface ListContactRequestsResponse {
  data: ContactRequestListItem[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ListContactRequestsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Query params for `GET /contact-requests/my-tasks` ("My Contact Requests").
 * Unlike {@link ListContactRequestsParams} there is no `status` filter — the
 * list is already self-scoped to the caller's assigned requests. The date
 * range filters on `created_at`.
 */
export interface ListMyAssignedContactRequestsParams {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
}
